import { useState, useRef, useEffect, useCallback } from "react";

const CYAN = "#00F0FF";
const VIOLET = "#7A4DFF";
const BG = "#02050A";

interface PinLockProps {
  userName: string;
  onUnlock: () => void;
  onLogout: () => void;
  onForgot: () => void;
}

export function PinLock({ userName, onUnlock, onLogout, onForgot }: PinLockProps) {
  const [digits, setDigits] = useState<string[]>(["", "", "", ""]);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const verify = useCallback(async (pin: string) => {
    setVerifying(true);
    setError("");
    try {
      const res = await fetch("/api/auth/pin/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pin }),
      });
      if (res.ok) {
        onUnlock();
      } else {
        const data = await res.json();
        setError(data.error || "Неверный PIN-код");
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setDigits(["", "", "", ""]);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
    } catch {
      setError("Ошибка соединения");
      setDigits(["", "", "", ""]);
    } finally {
      setVerifying(false);
    }
  }, [onUnlock]);

  const handleChange = (index: number, value: string) => {
    if (verifying) return;
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError("");

    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    if (digit && index === 3) {
      const pin = newDigits.join("");
      if (pin.length === 4) {
        verify(pin);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      const newDigits = [...digits];
      newDigits[index - 1] = "";
      setDigits(newDigits);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      const newDigits = pasted.split("");
      setDigits(newDigits);
      verify(pasted);
    }
  };

  const displayName = userName || "Пользователь";
  const firstName = displayName.split(" ")[0];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      background: BG,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Space Grotesk', 'Inter', sans-serif",
    }}>
      <div style={{
        background: "rgba(11,25,44,0.8)",
        border: "1px solid rgba(0,240,255,0.1)",
        borderRadius: 24,
        padding: "48px 40px 36px",
        maxWidth: 400,
        width: "90%",
        textAlign: "center",
        backdropFilter: "blur(20px)",
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 20,
          background: `linear-gradient(135deg, ${CYAN}20, ${VIOLET}20)`,
          border: `1px solid ${CYAN}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 24px",
          fontSize: 28,
        }}>
          🔒
        </div>

        <h1 style={{
          fontSize: 24, fontWeight: 700, color: "#fff",
          margin: "0 0 8px",
        }}>
          Здравствуйте, {firstName}!
        </h1>

        <p style={{
          fontSize: 14, color: "rgba(255,255,255,0.5)",
          margin: "0 0 32px",
        }}>
          Введите код для быстрого входа
        </p>

        <div
          style={{
            display: "flex", gap: 12, justifyContent: "center",
            marginBottom: 24,
            animation: shake ? "pinShake 0.4s ease" : undefined,
          }}
          onPaste={handlePaste}
        >
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={el => { inputRefs.current[i] = el; }}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              disabled={verifying}
              style={{
                width: 56, height: 64,
                borderRadius: 14,
                border: `2px solid ${error ? "rgba(255,77,77,0.5)" : digit ? CYAN + "60" : "rgba(0,240,255,0.15)"}`,
                background: digit ? "rgba(0,240,255,0.05)" : "rgba(30,42,71,0.4)",
                color: "#fff",
                fontSize: 24, fontWeight: 700,
                textAlign: "center",
                outline: "none",
                transition: "all 0.2s",
                caretColor: CYAN,
              }}
              onFocus={e => {
                e.target.style.borderColor = CYAN + "80";
                e.target.style.boxShadow = `0 0 0 3px ${CYAN}15`;
              }}
              onBlur={e => {
                e.target.style.borderColor = digit ? CYAN + "60" : "rgba(0,240,255,0.15)";
                e.target.style.boxShadow = "none";
              }}
            />
          ))}
        </div>

        {error && (
          <div style={{
            fontSize: 13, color: "#FF6B6B",
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {verifying && (
          <div style={{
            fontSize: 13, color: CYAN,
            marginBottom: 16,
          }}>
            Проверяю...
          </div>
        )}

        <div style={{
          display: "flex", justifyContent: "center", gap: 24,
          marginTop: 8,
        }}>
          <button
            onClick={onForgot}
            style={{
              background: "none", border: "none",
              color: "rgba(0,140,255,0.9)", fontSize: 14,
              cursor: "pointer", padding: "4px 0",
            }}
          >
            Не помню код
          </button>
          <button
            onClick={onLogout}
            style={{
              background: "none", border: "none",
              color: "rgba(0,140,255,0.9)", fontSize: 14,
              cursor: "pointer", padding: "4px 0",
            }}
          >
            Это не я
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pinShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-10px); }
          40% { transform: translateX(10px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
